<template>
  <div v-if="documentHeader" class="flex flex-col h-full overflow-hidden">
    <BaseDetailsPreviewEditHeader
      :is-horizontal-layout="isHorizontalLayout"
      :title="documentHeader?.documentNumber ? documentHeader.documentNumber : '-'"
      link-name="representatives-transactions"
      :is-modified="isModified"
      :mode="isModified ? 'edit' : 'view'"
      :is-edit-active="false"
      @horizontal-layout="(value: boolean) => (isHorizontalLayout = value)"
      @cancel="onCancel"
      @save="onSave" />
    <AppResizableContainer
      :layout="isHorizontalLayout ? 'vertical' : 'horizontal'"
      :minSize="300"
      class="overflow-auto">
      <template #headerTitle v-if="isHorizontalLayout">
        <div v-if="isHorizontalLayout">
          <nav
            role="section-nav"
            class="flex justify-between px-6 text-sm font-medium text-center text-gray-500 sm:px-0 min-h-9">
            <ul class="flex flex-wrap items-center gap-4 pl-4 -mb-px">
              <li
                v-for="tab in topTabs"
                :key="tab.name"
                @click="selectedTab = tab.name"
                class="py-1.5 cursor-pointer"
                :class="[
                  hasTabValidationError(tab)
                    ? 'text-red-500 border-b-2 border-red-500'
                    : hasTabDynamicValidationError(tab)
                      ? 'text-orange-500 border-b-2 border-orange-500'
                      : selectedTab === tab.name
                        ? 'border-b-2 border-theme-500'
                        : '',
                ]">
                <p>
                  {{ tab.label }}
                </p>
              </li>
            </ul>
          </nav>
        </div>
      </template>
      <template #headerContent>
        <!-- Horizontal Layout -->
        <template v-if="isHorizontalLayout">
          <PerfectScrollbar class="w-full h-full py-4 space-y-2" ref="scrollbarId">
            <component
              :is="currentComponent"
              ref="currentComponentRef"
              :mode="mode"
              v-model="modifiedDocumentHeader"
              :validations="$v"
              :resetState="resetState" />
          </PerfectScrollbar>
        </template>
        <template v-else>
          <PerfectScrollbar class="w-full h-full py-4 space-y-2" ref="scrollbarId">
            <BaseAccordion
              v-for="tab in topTabs"
              :key="tab.name"
              :name="tab.label"
              :error="tab.validators && hasTabValidationError(tab)"
              :warning="!hasTabValidationError(tab) && hasTabDynamicValidationError(tab)">
              <component
                :is="currentComponent"
                :documentHeader="modifiedDocumentHeader"
                :mode="mode"
                v-model="modifiedDocumentHeader"
                :validations="$v"
                :resetState="resetState" />
            </BaseAccordion>
          </PerfectScrollbar>
        </template>
      </template>
      <template #detailsContent>
        <TransactionsDetailsTable
          v-if="modifiedDocumentHeader?.representative"
          v-model="modifiedDocumentHeader"
          :field-requirements="fieldRequirements"
          @init="nextTick(() => (isModified = false))" />
      </template>
    </AppResizableContainer>

    <BaseDrawerConfirm
      :is-active="confirmModalActive"
      @cancel="confirmModalActive = false"
      @confirm="onDiscardChanges" />
  </div>
</template>

<script setup lang="ts">
import { computed, markRaw, nextTick, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import useVuelidate from '@vuelidate/core';
import { helpers, required } from '@vuelidate/validators';
import {
  addOrUpdateDCDocumentHeader,
  getDCDocumentHeaderById,
  getDocumentFieldRequirements,
} from '@/services/api/dataCollection';

import AppResizableContainer from '@/components/common/AppResizableContainer.vue';
import BaseDetailsPreviewEditHeader from '@/components/common/BaseDetailsPreviewEditHeader.vue';
import BaseAccordion from '@/components/common/BaseAccordion.vue';
import TransactionsDetailsTabProperties from '@/components/traders/TransactionsDetailsTabProperties.vue';
import TransactionDetailsTabCustomer from '@/components/traders/TransactionDetailsTabCustomer.vue';
import TransactionDetailsTabRecipient from '@/components/traders/TransactionDetailsTabRecipient.vue';
import TransactionDetailsTabDocument from '@/components/traders/TransactionDetailsTabDocument.vue';
import TransactionsDetailsTable from '@/components/traders/TransactionsDetailsTable.vue';
import BaseDrawerConfirm from '@/components/common/BaseDrawerConfirm.vue';
import type { DCDocumentHeader } from '@/types/DataCollection';
import type { PerfectScrollbarExpose } from 'vue3-perfect-scrollbar';
import { deepCopy } from '@/utils/data';

const { t: $t } = useI18n();
const route = useRoute();
const documentHeader = ref<Partial<DCDocumentHeader> | null>(null);
const modifiedDocumentHeader = ref<Partial<DCDocumentHeader> | null>(null);
const isHorizontalLayout = ref(true);
const isModified = ref<boolean>(false);
const currentComponentRef = ref();
const mode = computed<'view' | 'edit'>(() => {
  if (!documentHeader.value?.representativeDocumentType) {
    return 'view';
  }
  return documentHeader.value.representativeDocumentType.allowChange ? 'edit' : 'view';
});

const scrollbarId = ref<PerfectScrollbarExpose>();

const selectedTab = ref('document');

// Dynamic validation state - field requirements from server
const fieldRequirements = ref<{
  header: Record<string, boolean>;
  details: Record<string, boolean>;
} | null>(null);
const topTabs = ref([
  {
    name: 'document',
    label: $t('representative.document'),
    component: markRaw(TransactionDetailsTabDocument),
    validators: [
      'documentHeaderRowId',
      'officialDate',
      'source',
      'companyId',
      'companySiteId',
      'representativeId',
      'representativeSiteId',
      'companyDocumentTypeId',
      'representativeDocumentTypeId',
      'representativeDocumentTypeCode',
      'documentNumber',
      'documentDescription',
      'representativeDocumentSeriesCode',
      'representativePaymentTypeId',
      'representativePaymentTypeCode',
      'companyPaymentTypeId',
    ],
  },
  {
    name: 'customer',
    label: $t('representative.customer'),
    component: markRaw(TransactionDetailsTabCustomer),
    validators: [
      'representativeCustomerId',
      'companyCustomerId',
      'representativeCustomerCode',
      'representativeCustomerTaxCode',
      'representativeCustomerName',
      'representativeCustomerAddress',
      'representativeCustomerCity',
      'customerSiteCode',
      'customerSiteName',
      'customerSiteAddress',
      'customerSiteCity',
    ],
  },
  {
    name: 'recipient',
    label: $t('representative.recipient'),
    component: markRaw(TransactionDetailsTabRecipient),
    validators: [
      'representativeRecipientId',
      'companyRecipientId',
      'representativeRecipientCode',
      'representativeRecipientName',
      'representativeRecipientAddress',
      'representativeRecipientCity',
      'recipientSiteId',
      'recipientSiteCode',
      'recipientSiteName',
      'recipientSiteAddress',
      'recipientSiteCity',
    ],
  },
  {
    name: 'properties',
    label: $t('representative.properties'),
    component: markRaw(TransactionsDetailsTabProperties),
    validators: [
      'detailsCount',
      'totalGrossAmount',
      'totalNetAmount',
      'totalVatAmount',
      'totalQuantity',
      'isApproved',
      'approvalUserId',
      'isInvalid',
      'remarks',
      'userExport',
      'exported',
    ],
  },
]);

const currentComponent = computed(() => {
  const selected = topTabs.value.find((tab) => tab.name === selectedTab.value);
  return selected ? selected.component : null;
});

// Validation
const validationModel = computed(() => ({
  // Document tab fields
  documentHeaderRowId: modifiedDocumentHeader.value?.documentHeaderRowId || '',
  source: modifiedDocumentHeader.value?.source || '',
  companyId: modifiedDocumentHeader.value?.companyId || '',
  companySiteId: modifiedDocumentHeader.value?.companySiteId || '',
  representativeId: modifiedDocumentHeader.value?.representativeId || '',
  representativeSiteId: modifiedDocumentHeader.value?.representativeSiteId || '',
  companyDocumentTypeId: modifiedDocumentHeader.value?.companyDocumentTypeId || '',
  representativeDocumentTypeId: modifiedDocumentHeader.value?.representativeDocumentTypeId || '',
  representativeDocumentTypeCode:
    modifiedDocumentHeader.value?.representativeDocumentTypeCode || '',
  documentNumber: modifiedDocumentHeader.value?.documentNumber || '',
  documentDescription: modifiedDocumentHeader.value?.documentDescription || '',
  representativeDocumentSeriesCode:
    modifiedDocumentHeader.value?.representativeDocumentSeriesCode || '',
  representativePaymentTypeId: modifiedDocumentHeader.value?.representativePaymentTypeId || '',
  representativePaymentTypeCode: modifiedDocumentHeader.value?.representativePaymentTypeCode || '',
  companyPaymentTypeId: modifiedDocumentHeader.value?.companyPaymentTypeId || '',

  // Customer tab fields
  representativeCustomerId: modifiedDocumentHeader.value?.representativeCustomerId || '',
  companyCustomerId: modifiedDocumentHeader.value?.companyCustomerId || '',
  representativeCustomerCode: modifiedDocumentHeader.value?.representativeCustomerCode || '',
  representativeCustomerTaxCode: modifiedDocumentHeader.value?.representativeCustomerTaxCode || '',
  representativeCustomerName: modifiedDocumentHeader.value?.representativeCustomerName || '',
  representativeCustomerAddress: modifiedDocumentHeader.value?.representativeCustomerAddress || '',
  representativeCustomerCity: modifiedDocumentHeader.value?.representativeCustomerCity || '',
  customerSiteCode: modifiedDocumentHeader.value?.customerSiteCode || '',
  customerSiteName: modifiedDocumentHeader.value?.customerSiteName || '',
  customerSiteAddress: modifiedDocumentHeader.value?.customerSiteAddress || '',
  customerSiteCity: modifiedDocumentHeader.value?.customerSiteCity || '',

  // Recipient tab fields
  representativeRecipientId: modifiedDocumentHeader.value?.representativeRecipientId || '',
  companyRecipientId: modifiedDocumentHeader.value?.companyRecipientId || '',
  representativeRecipientCode: modifiedDocumentHeader.value?.representativeRecipientCode || '',
  representativeRecipientName: modifiedDocumentHeader.value?.representativeRecipientName || '',
  representativeRecipientAddress:
    modifiedDocumentHeader.value?.representativeRecipientAddress || '',
  representativeRecipientCity: modifiedDocumentHeader.value?.representativeRecipientCity || '',
  recipientSiteId: modifiedDocumentHeader.value?.recipientSiteId || '',
  recipientSiteCode: modifiedDocumentHeader.value?.recipientSiteCode || '',
  recipientSiteName: modifiedDocumentHeader.value?.recipientSiteName || '',
  recipientSiteAddress: modifiedDocumentHeader.value?.recipientSiteAddress || '',
  recipientSiteCity: modifiedDocumentHeader.value?.recipientSiteCity || '',

  // Properties tab fields
  detailsCount: modifiedDocumentHeader.value?.detailsCount || 0,
  totalGrossAmount: modifiedDocumentHeader.value?.totalGrossAmount || 0,
  totalNetAmount: modifiedDocumentHeader.value?.totalNetAmount || 0,
  totalVatAmount: modifiedDocumentHeader.value?.totalVatAmount || 0,
  totalQuantity: modifiedDocumentHeader.value?.totalQuantity || 0,
  isApproved: modifiedDocumentHeader.value?.isApproved || false,
  approvalUserId: modifiedDocumentHeader.value?.approvalUserId || '',
  isInvalid: modifiedDocumentHeader.value?.isInvalid || false,
  officialDate: modifiedDocumentHeader.value?.officialDate || '',
  remarks: modifiedDocumentHeader.value?.remarks || '',
  userExport: modifiedDocumentHeader.value?.userExport || '',
  exported: modifiedDocumentHeader.value?.exported || false,
}));

// Helper to convert PascalCase to camelCase
function pascalToCamelCase(str: string): string {
  const camelCase = str.charAt(0).toLowerCase() + str.slice(1);
  return camelCase.replace(/ID$/, 'Id');
}

// Dynamic rules generation based on fieldRequirements
const rules = computed(() => {
  const baseRules: any = {
    companyId: {
      required: helpers.withMessage($t('messages.errors.required'), required),
    },
    companySiteId: {
      required: helpers.withMessage($t('messages.errors.required'), required),
    },
    representativeId: {
      required: helpers.withMessage($t('messages.errors.required'), required),
    },
    representativeDocumentTypeCode: {
      required: helpers.withMessage($t('messages.errors.required'), required),
    },
  };

  // Add dynamic rules based on server field requirements
  if (fieldRequirements.value?.header) {
    Object.keys(fieldRequirements.value.header).forEach((pascalFieldName) => {
      if (fieldRequirements.value!.header[pascalFieldName] === true) {
        const camelFieldName = pascalToCamelCase(pascalFieldName);
        if (!baseRules[camelFieldName]) baseRules[camelFieldName] = {};
        baseRules[camelFieldName].dynamicRequired = helpers.withMessage(
          $t('messages.errors.required'),
          (value: any) => !isEmpty(value)
        );
      }
    });
  }

  return baseRules;
});

const $v = useVuelidate(rules, validationModel, { $lazy: true });

// Function to check if a tab has validation errors (blocking - red)
function hasTabValidationError(tab: any): boolean {
  if (!tab.validators || !$v.value || mode.value === 'view') return false;

  return tab.validators.some((validator: string) => {
    const validationField = $v.value[validator];
    if (!validationField) return false;

    // Check only for blocking rules (required, not dynamicRequired)
    return Object.keys(validationField).some((rule) => {
      if (rule === 'dynamicRequired') return false;
      const ruleValidation = validationField[rule];
      return ruleValidation && typeof ruleValidation === 'object' && ruleValidation.$invalid;
    });
  });
}

// Function to check if a tab has dynamic validation errors (non-blocking - blue)
function hasTabDynamicValidationError(tab: any): boolean {
  if (!tab.validators || !$v.value || mode.value === 'view') return false;

  return tab.validators.some((validator: string) => {
    const validationField = $v.value[validator];
    if (!validationField) return false;

    // Check only for dynamicRequired rule
    const dynamicRule = validationField.dynamicRequired;
    return dynamicRule && typeof dynamicRule === 'object' && dynamicRule.$invalid;
  });
}

// Helper to check if a value is empty
function isEmpty(value: any): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string' && value.trim() === '') return true;
  if (typeof value === 'number' && isNaN(value)) return true;
  return false;
}

// Fetch field requirements from server
async function fetchFieldRequirements() {
  if (!modifiedDocumentHeader.value?.representative?.id) {
    fieldRequirements.value = null;
    return;
  }

  const requirements = await getDocumentFieldRequirements(
    modifiedDocumentHeader.value.representative.id
  );

  if (requirements) {
    fieldRequirements.value = requirements;
  }
}

onMounted(async () => {
  await loadDocument();
  $v.value.$touch();
  isModified.value = false;
});

async function loadDocument() {
  const documentId = route.params.id as string;
  if (documentId) {
    const res = await getDCDocumentHeaderById(documentId);
    if (res) {
      documentHeader.value = res;
      modifiedDocumentHeader.value = deepCopy<Partial<DCDocumentHeader>>(documentHeader.value);

      // Fetch field requirements for visual feedback
      await fetchFieldRequirements();
    }
  }
}

const isBackMode = ref(false);
const confirmModalActive = ref(false);
const resetState = ref(false);
const router = useRouter();
function onCancel(backMode: string) {
  if (isModified.value) {
    confirmModalActive.value = true;
    if (backMode == 'back') {
      isBackMode.value = true;
    }
    return;
  } else {
    router.push('/representatives-transactions');
  }
}

function onDiscardChanges() {
  confirmModalActive.value = false;
  if (documentHeader.value)
    modifiedDocumentHeader.value = deepCopy<Partial<DCDocumentHeader>>(documentHeader.value);
  resetState.value = true;
  $v.value.$touch();
  if (!documentHeader.value?.createdDate || isBackMode.value) {
    router.push('/representatives-transactions');
  }
}

async function onSave() {
  $v.value.$touch();

  if (isModified.value && modifiedDocumentHeader.value) {
    // Save document - server will calculate and return isInvalid property
    const savedDocument = await addOrUpdateDCDocumentHeader(modifiedDocumentHeader.value);

    if (savedDocument) {
      // Update local state with server response (includes isInvalid)
      documentHeader.value = savedDocument;
      modifiedDocumentHeader.value = deepCopy<Partial<DCDocumentHeader>>(savedDocument);
      isModified.value = false;
    }
  }
}

watch(
  () => modifiedDocumentHeader.value,
  (newData) => {
    isModified.value = JSON.stringify(newData) !== JSON.stringify(documentHeader.value);
    $v.value.$touch();
  },
  { deep: true, immediate: true }
);
</script>
